import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Input,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  FilterList as FilterIcon,
  History as HistoryIcon,
  LocationOn as LocationOnIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from 'utils/api';
import { useLocation } from 'utils/location';
import { buildSearchScope, newSearchParams, searchUrl } from 'utils/searchScope';
import { useSearchViewport } from '../SearchViewportContext';

const HISTORY_LIMIT = 50;

export const normalizeHistoryQuery = (query) =>
  query.trim().replace(/\s+/g, ' ').toLowerCase();

const decodeUrlQuery = (query) => {
  try {
    return decodeURIComponent(query);
  } catch (error) {
    return query;
  }
};

const SearchBar = ({
  onMapToggle,
  onFilterClick,
  isMapActive = false,
  filterAnchorEl = null,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const listboxId = useId();
  const inputRef = useRef(null);
  const query = searchParams.get('query') || '';
  const [q, setQ] = useState(() => decodeUrlQuery(query));
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyUnavailable, setHistoryUnavailable] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [suggestionResult, setSuggestionResult] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const composing = useRef(false);
  const userLocation = useLocation();
  const viewport = useSearchViewport();
  const scope = buildSearchScope(searchParams, userLocation, viewport);
  const suggestionKey = searchUrl('suggestions', scope, { query: q.trim() });
  const suggestions = suggestionResult?.key === suggestionKey ? suggestionResult.entries : [];

  useEffect(() => {
    setActiveIndex(-1);
    if (!isFocused || isComposing || q.trim().length < 2 || q.trim().length > 120) return undefined;
    let current = true;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await api.get(suggestionKey, { signal: controller.signal });
        if (current) {
          setActiveIndex(-1);
          setSuggestionResult({
            key: suggestionKey,
            entries: (response.suggestions || []).slice(0, 8).map(({ text }) => ({
              id: `suggestion-${text}`, query: text, suggestion: true,
            })),
          });
        }
      } catch (error) {
        if (current) setSuggestionResult({ key: suggestionKey, entries: [] });
      }
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [suggestionKey, isFocused, isComposing, q]);

  useEffect(() => {
    setQ(decodeUrlQuery(query));
    setActiveIndex(-1);
  }, [query]);

  const filteredHistory = useMemo(() => {
    const normalizedInput = normalizeHistoryQuery(q);
    return history
      .filter((entry) => !normalizedInput
        || normalizeHistoryQuery(entry.query).includes(normalizedInput))
      .slice(0, HISTORY_LIMIT);
  }, [history, q]);

  const options = q.trim()
    ? [...suggestions, ...filteredHistory.filter((entry) => !suggestions.some(
      (suggestion) => normalizeHistoryQuery(suggestion.query) === normalizeHistoryQuery(entry.query),
    )).slice(0, 3)]
    : filteredHistory;

  const loadHistory = async () => {
    if (historyLoaded || historyUnavailable) return;

    try {
      const response = await api.get('/search-history');
      const entries = Array.isArray(response) ? response.slice(0, HISTORY_LIMIT) : [];
      setHistory(entries);
      setHistoryLoaded(true);
    } catch (error) {
      setHistoryUnavailable(true);
    }
  };

  const navigateToSearch = (searchQuery) => {
    const nextParams = newSearchParams(searchParams, searchQuery, scope);
    navigate({ pathname: '/search', search: nextParams.toString() });
  };

  const recordHistory = (searchQuery) => {
    const normalizedQuery = normalizeHistoryQuery(searchQuery);
    const replacedEntry = history.find(
      (entry) => normalizeHistoryQuery(entry.query) === normalizedQuery,
    );
    const optimisticEntry = {
      id: `pending-${Date.now()}`,
      query: searchQuery,
      searchedAt: new Date().toISOString(),
    };
    setHistory([
      optimisticEntry,
      ...history.filter(
        (entry) => normalizeHistoryQuery(entry.query) !== normalizedQuery,
      ),
    ].slice(0, HISTORY_LIMIT));

    api.post('/search-history', { query: searchQuery })
      .then((savedEntry) => {
        setHistory((currentHistory) => [
          savedEntry,
          ...currentHistory.filter(
            (entry) => entry.id !== optimisticEntry.id
              && normalizeHistoryQuery(entry.query) !== normalizedQuery,
          ),
        ].slice(0, HISTORY_LIMIT));
      })
      .catch(() => {
        setHistory((currentHistory) => {
          const withoutOptimisticEntry = currentHistory.filter(
            (entry) => entry.id !== optimisticEntry.id,
          );
          return replacedEntry
            ? [replacedEntry, ...withoutOptimisticEntry].slice(0, HISTORY_LIMIT)
            : withoutOptimisticEntry;
        });
      });
  };

  const submitSearch = (value) => {
    const searchQuery = value.trim();
    if (!searchQuery || composing.current) return;

    setQ(searchQuery);
    setIsOpen(false);
    setActiveIndex(-1);
    recordHistory(searchQuery);
    navigateToSearch(searchQuery);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitSearch(q);
  };

  const handleKeyDown = (event) => {
    if (composing.current || event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === 'Escape') {
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (options.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((current) => (current + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((current) => (
        current <= 0 ? options.length - 1 : current - 1
      ));
    } else if (event.key === 'Enter' && isOpen && activeIndex >= 0) {
      event.preventDefault();
      if (options[activeIndex]) submitSearch(options[activeIndex].query);
    }
  };

  const handleDelete = async (event, entry) => {
    event.preventDefault();
    event.stopPropagation();
    const previousHistory = history;
    setHistory((currentHistory) => currentHistory.filter((item) => item.id !== entry.id));
    setActiveIndex(-1);

    try {
      await api.delete(`/search-history/${entry.id}`);
    } catch (error) {
      setHistory(previousHistory);
      enqueueSnackbar('Search history could not be deleted. Try again.', { variant: 'error' });
    }
  };

  const handleClearAll = async (event) => {
    event.preventDefault();
    const previousHistory = history;
    setHistory([]);
    setIsOpen(false);

    try {
      await api.delete('/search-history');
    } catch (error) {
      setHistory(previousHistory);
      setIsOpen(previousHistory.length > 0 && isFocused);
      enqueueSnackbar('Search history could not be cleared. Try again.', { variant: 'error' });
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setActiveIndex(-1);
    setIsOpen(true);
    loadHistory();
  };

  const handleBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsFocused(false);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const showHistory = isOpen && !isComposing && options.length > 0;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      onBlur={handleBlur}
      sx={{ maxWidth: 800, width: '100%', position: 'relative' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          background: showHistory ? '#fff' : '#ededed',
          border: '1px solid',
          borderColor: showHistory ? '#1f1f1f' : '#ddd',
          borderBottomColor: showHistory ? '#e2e2e2' : undefined,
          transition: 'border-color 120ms ease, background-color 120ms ease',
        }}
      >
        <Input
          color="primary"
          disableUnderline
          fullWidth
          inputRef={inputRef}
          inputProps={{
            style: { padding: 16, background: 'transparent' },
            'aria-label': 'Search offers',
            'aria-autocomplete': 'list',
            'aria-controls': showHistory ? listboxId : undefined,
            'aria-expanded': showHistory,
            'aria-activedescendant': showHistory && activeIndex >= 0 && activeIndex < options.length
              ? `${listboxId}-option-${activeIndex}`
              : undefined,
            role: 'combobox',
          }}
          placeholder="Search offers"
          sx={{ px: 1, background: 'transparent', flex: 1 }}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => { composing.current = true; setIsComposing(true); }}
          onCompositionEnd={() => { composing.current = false; setIsComposing(false); }}
          onChange={(event) => {
            const nextQuery = event.target.value;
            setQ(nextQuery);
            setActiveIndex(-1);
            setIsOpen(true);
          }}
          value={q}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
          {q && (
            <Tooltip title="Clear search">
              <IconButton
                aria-label="Clear search"
                onClick={() => {
                  setQ('');
                  setActiveIndex(-1);
                  setIsOpen(history.length > 0);
                  inputRef.current?.focus();
                }}
                size="small"
                sx={{ color: '#808080', '&:hover': { color: '#000' } }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Search">
            <IconButton
              aria-label="Search"
              type="submit"
              size="small"
              sx={{ color: '#808080', '&:hover': { color: '#000' } }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Map view">
            <IconButton
              aria-label="Map view"
              onClick={onMapToggle}
              size="small"
              sx={{
                color: isMapActive ? '#000' : '#808080',
                '&:hover': { color: '#000' },
              }}
            >
              <LocationOnIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Filter">
            <IconButton
              aria-label="Filter"
              onClick={onFilterClick}
              size="small"
              sx={{
                color: filterAnchorEl ? '#000' : '#808080',
                '&:hover': { color: '#000' },
              }}
            >
              <FilterIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {showHistory && (
        <Paper
          square
          elevation={8}
          sx={{
            position: 'absolute',
            zIndex: 1400,
            top: '100%',
            left: 0,
            right: 0,
            border: '1px solid #1f1f1f',
            borderTop: 0,
            backgroundColor: '#fff',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              minHeight: 42,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #ececec',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#555', fontWeight: 700, letterSpacing: '0.04em' }}
            >
              {q.trim() ? 'Search suggestions' : 'Recent searches'}
            </Typography>
            {!q.trim() && history.length > 0 && <Button
              type="button"
              size="small"
              onClick={handleClearAll}
              sx={{ color: '#555', textTransform: 'none', minWidth: 0 }}
            >
              Clear all
            </Button>}
          </Box>

          <List
            id={listboxId}
            role="listbox"
            disablePadding
            sx={{ maxHeight: { xs: '45vh', sm: 320 }, overflowY: 'auto' }}
          >
            {options.map((entry, index) => {
              const isActive = index === activeIndex;
              return (
                <ListItemButton
                  id={`${listboxId}-option-${index}`}
                  key={entry.id}
                  role="option"
                  aria-selected={isActive}
                  selected={isActive}
                  onClick={() => submitSearch(entry.query)}
                  sx={{
                    minHeight: 48,
                    pl: 1.5,
                    pr: 0.75,
                    borderLeft: '3px solid',
                    borderLeftColor: isActive ? '#111' : 'transparent',
                    '&:hover': { backgroundColor: '#f1f1f1' },
                    '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: '#f1f1f1' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: '#777' }}>
                    {entry.suggestion ? <SearchIcon fontSize="small" /> : <HistoryIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={entry.query}
                    primaryTypographyProps={{ noWrap: true, fontSize: 15 }}
                  />
                  {!entry.suggestion && <Tooltip title="Remove from search history">
                    <IconButton
                      type="button"
                      size="small"
                      aria-label={`Remove ${entry.query} from search history`}
                      onClick={(event) => handleDelete(event, entry)}
                      sx={{ color: '#888', '&:hover': { color: '#111' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>}
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default SearchBar;
